/**
 * UNIT-ADD-MODAL
 * Create new modal with assistant
 */
import { useState } from "react"
import Modal from "@/components/modal"
import CardButton from "@/components/card-button"
import { faWandMagicSparkles, faArrowLeft, IconDefinition } from "@fortawesome/free-solid-svg-icons"
import { t } from "i18next"
import { useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import unitStore from "@/features/risks/entities/unit/model/unit.store"
import { ensureUnitSaved } from "../../utils/ensureUnitSaved"
import UnitEditInfos from "../UnitEditInfos"
import { toast } from "react-toastify"
import { RiskAddMode } from "../../../risk/model/risk.types"
import { Unit } from "@/features/risks/entities/unit/model/unit.types"

interface WizardOptions {
  icon: IconDefinition
  id: RiskAddMode
  title: string
}

interface Props {
  addRisks?: boolean
  onClose: () => void
}

const wizardOptions: WizardOptions[] = [
  {
    id: "assistant",
    title: "Ajouter des risques",
    icon: faWandMagicSparkles
  },
  {
    id: "later",
    title: "Ajouter des risques plus tard",
    icon: faArrowLeft
  }
]

export default function UnitAddModal({ onClose, addRisks = true }: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const unit = useAppSelector(unitStore.selectors.draft)
  const units = useAppSelector(unitStore.selectors.list)

  const [selectedCard, setSelectedCard] = useState<WizardOptions["id"] | null>("assistant")
  const [step, setStep] = useState(1)
  const [savedUnit, setSavedUnit] = useState<Unit | null>(null)

  // Step 1: Validate name and create unit
  async function handleStep1() {
    if (!unit.name.length) {
      toast(t("units_name_empty"), { type: "error" })
      return
    }

    const existingUnit = units.find((u) => u.name === unit.name.trim())
    if (existingUnit) {
      toast(t("Une unité avec ce nom existe déjà"), { type: "error" })
      return
    }

    // Create the unit
    const created = await ensureUnitSaved(unit, unit, units, dispatch)
    if (!created) {
      toast(t("L'unité de travail n'a pu être crée."), { type: "error" })
      return
    }

    setSavedUnit(created)

    // If not adding risks, close immediately
    if (!addRisks) {
      onClose()
      return
    }

    // Otherwise go to step 2 to choose what to do with risks
    setStep(2)
  }

  // Step 2: Choose what to do with risks (unit is already created)
  function handleStep2() {
    if (!savedUnit) return

    if (selectedCard === "assistant") {
      navigate("/risks/risks-add", { state: { backTo: "/risks/units" } })
    } else {
      // "later" or default: just close
      onClose()
    }
  }

  const onNext = () => (step === 1 ? handleStep1() : handleStep2())

  return (
    <Modal
      title={step === 1 ? t("units_utils_create_new_node_short") : t("Ajouter des risques à l'unité de travail ?")}
      isCloseButtonVisible
      displayOwl={step === 2}
      displayOwlTop={200}
      onClose={onClose}
      onNext={() => onNext()}
      disableKeyEvents
      isLarge>
      <div className="h-fit w-auto">
        {step === 1 && <UnitEditInfos />}

        {step === 2 && (
          <div className="w-auto">
            <div className="grey-t mb-[22px]">
              {t("unit")} : <b>{`« ${savedUnit?.name} »`}</b>
            </div>
            <div className="flex justify-center gap-4">
              {wizardOptions.map((card) => (
                <CardButton
                  key={card.id}
                  isCta={selectedCard === card.id}
                  icon={card.icon}
                  title={card.title}
                  onClick={() => setSelectedCard(card.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
